import { z } from "zod";

export const provenanceStatus = z.enum(["VERIFIED", "INCOMPLETE", "UNKNOWN"]);
export const provenanceCause = z.object({
  code: z.string().min(1),
  nodeId: z.string().min(1),
  project: z.string(),
  step: z.string().nullish(),
  reason: z.string().min(1),
  recommendation: z.string(),
});
export const sourceEvidence = z.object({
  kind: z.enum(["SourceContent", "DependencyManifest", "DependencyLock"]),
  status: provenanceStatus,
  reference: z.string(),
  hash: z.string().nullish(),
  reason: z.string(),
});
export const provenanceTransformation = z.object({
  nodeId: z.string().min(1),
  step: z.string(),
  entrypoint: z.string(),
  preservation: z.enum(["preserved", "broken", "unaffected"]),
  origin: z.enum(["builtin", "custom"]),
  scriptIdentity: z.string(),
  contractFingerprint: z.string(),
});
export const provenanceDeclaration = z.object({
  version: z.literal(1),
  producerVersion: z.string().default(""),
  productionId: z.string().min(1).max(100),
  nodeId: z.string().min(1),
  transformations: z.array(provenanceTransformation),
  dependencies: z.array(z.object({
    nodeId: z.string().min(1),
    productionId: z.string().nullish(),
  })),
});
export type ProvenanceDeclaration = z.infer<typeof provenanceDeclaration>;

export const provenanceReport = z.object({
  version: z.literal(1),
  productionId: z.string().min(1).max(100),
  producedAt: z.string().datetime({ offset: true }),
  producerVersion: z.string(),
  repository: z.string(),
  revision: z.string(),
  nodeId: z.string().min(1),
  project: z.string(),
  target: z.string(),
  projectHash: z.string().min(1),
  targetHash: z.string().min(1),
  status: provenanceStatus,
  evidence: z.array(sourceEvidence),
  transformations: z.array(provenanceTransformation),
  dependencies: z.array(z.object({
    nodeId: z.string().min(1),
    productionId: z.string().nullish(),
    status: provenanceStatus,
  })),
  causes: z.array(provenanceCause),
  outputs: z.array(z.string()),
}).superRefine((report, context) => {
  if (report.status === "VERIFIED" && (
    report.causes.length > 0 ||
    !report.evidence.some((item) => item.kind === "SourceContent") ||
    report.evidence.some((item) => item.status !== "VERIFIED" || !item.hash) ||
    report.transformations.some((item) => item.preservation === "broken" || item.origin !== "builtin") ||
    report.dependencies.some((item) => item.status !== "VERIFIED")
  )) {
    context.addIssue({ code: "custom", message: "Verified provenance contains unsupported evidence or contracts" });
  }
});

export type ProvenanceStatus = z.infer<typeof provenanceStatus>;
export type ProvenanceReport = z.infer<typeof provenanceReport>;
export type ProvenanceTransformation = z.infer<typeof provenanceTransformation>;

export interface ProvenanceArtifact {
  productionId?: string | null;
  reportAvailability?: "available" | "missing" | "pending";
  projectId: string;
  project: string;
  target: string;
  projectHash: string;
  targetHash: string;
  buildId: string;
  producerBuildId?: string | null;
  association: "produced" | "reused" | "carried-forward" | "unknown";
  status: ProvenanceStatus;
  report: ProvenanceReport | null;
}
export interface ProvenanceResponse {
  computation?: "pending" | "complete";
  artifacts: ProvenanceArtifact[];
  verified: number;
  total: number;
  relatedProductions?: Array<{ buildId: string; report: ProvenanceReport }>;
}

export function summarizeProvenance(artifacts: ProvenanceArtifact[]): ProvenanceResponse {
  return {
    artifacts,
    verified: artifacts.filter((artifact) => artifact.status === "VERIFIED").length,
    total: artifacts.length,
  };
}

export function provenanceSignature(artifact: ProvenanceArtifact): string {
  return JSON.stringify({
    status: artifact.status,
    missing: artifact.report === null,
    projectHash: artifact.projectHash,
    targetHash: artifact.targetHash,
    evidence: artifact.report?.evidence,
    dependencies: artifact.report?.dependencies,
    causes: artifact.report?.causes,
    transformations: artifact.report?.transformations,
  });
}
